// The one way to join Telegram and the web: the bot's /sync_to_web gives a short code, typed here.
// Signed in it links that Telegram account; signed out it signs in as the account linked to it. If that
// Telegram already has its own Chefi account, the person picks which side wins and the two are merged.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Body, Button, Field } from './ui';
import ErrorNote from './ErrorNote';
import { useAccount } from '../store/account';
import * as auth from '../platform/auth';
import { choose, notify } from './Dialogs';
import { syncNow } from '../store/cloud';

export default function SyncBox({ initialCode = '' }: { initialCode?: string }) {
  const { t } = useTranslation();
  const { user, busy, run } = useAccount();
  const [code, setCode] = useState(initialCode);
  const [bot, setBot] = useState<string | null>(null);
  const [done, setDone] = useState<'linked' | 'signedIn' | 'merged' | null>(null);
  useEffect(() => {
    auth.fetchConfig().then(c => setBot(c.telegramBot)).catch(() => {});
  }, []);
  if (done) return <Body>{t(done === 'merged' ? 'sync.merged' : done === 'linked' ? 'sync.linked' : 'sync.signedInNow')}</Body>;
  return (
    <>
      <Body secondary>{bot ? t('sync.how', { bot: `@${bot}` }) : t('sync.howNoBot')}</Body>
      <Field label={t('sync.codeLabel')} value={code} onChangeText={setCode} placeholder="XXXX-XXXX" autoCapitalize="characters" autoCorrect={false} maxLength={12} />
      <Button
        label={user ? t('sync.link') : t('sync.signIn')} icon="lock" loading={busy} disabled={busy || code.replace(/[\s-]/g, '').length < 8}
        onPress={() => run(async () => {
          let r: Awaited<ReturnType<typeof auth.syncWithCode>>;
          try {
            r = await auth.syncWithCode(code);
          } catch (e) {
            if (!(e instanceof auth.ApiError) || e.code !== 'merge_required') throw e;
            const other = (e.data?.other as { displayName?: string } | undefined)?.displayName || t('sync.mergeUnnamed');
            const winner = await choose(t('sync.mergeTitle'), t('sync.mergeBody', { other }), [
              { value: 'web' as const, label: t('sync.mergeKeepWeb') },
              { value: 'telegram' as const, label: t('sync.mergeKeepTelegram', { other }) },
            ]);
            if (!winner) return;
            r = await auth.syncWithCode(code, winner);
          }
          setDone(r.merged ? 'merged' : r.linked ? 'linked' : 'signedIn');
          if (r.merged) {
            syncNow(); // the other account's data is waiting to be merged into this device's copy
            notify(t('sync.merged')); // a notice: the box itself gives way to "Telegram is linked"
          }
          return { user: r.user };
        }, true)}
      />
      <ErrorNote />
    </>
  );
}
